class InfiniteScroll extends HTMLElement {
  constructor() {
    super();
    this.observer = null;
    this.controller = null;
    this.loading = false;
    this.restoring = false;
    this.scrollFrame = null;

    this.handleRetry = (event) => {
      event.preventDefault();
      this.loadNextPage();
    };

    this.handlePreviousRetry = (event) => {
      event.preventDefault();
      this.restorePreviousPages();
    };

    this.handleScroll = () => this.queueVisiblePageUpdate();
    this.handlePageHide = () => this.saveHistoryState();
    this.handlePageShow = (event) => {
      if (event.persisted) this.restoreSavedPosition(this.readSavedState());
    };
    this.handleItemClick = (event) => this.saveClickedProduct(event);
  }

  connectedCallback() {
    this.items = this.querySelector('[data-infinite-scroll-items]');
    this.sentinel = this.querySelector('[data-infinite-scroll-sentinel]');
    this.status = this.querySelector('[data-infinite-scroll-status]');
    this.fallback = this.querySelector('[data-infinite-scroll-fallback]');
    this.previousFallback = this.querySelector('[data-infinite-scroll-previous-fallback]');
    this.historyEnabled = this.hasAttribute('data-infinite-scroll-history');
    this.activePage = this.currentPage;

    if (!this.items || !this.sentinel || !('IntersectionObserver' in window)) return;

    if (this.fallback) {
      this.fallback.hidden = true;
      this.fallback.addEventListener('click', this.handleRetry);
    }

    if (this.previousFallback) {
      this.previousFallback.hidden = true;
      this.previousFallback.addEventListener('click', this.handlePreviousRetry);
    }

    this.observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) this.loadNextPage();
      },
      { rootMargin: '0px 0px 1200px 0px' }
    );

    this.refreshDynamicContent();

    if (this.historyEnabled) {
      if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
      window.addEventListener('scroll', this.handleScroll, { passive: true });
      window.addEventListener('pagehide', this.handlePageHide);
      window.addEventListener('pageshow', this.handlePageShow);
      this.addEventListener('click', this.handleItemClick, true);
    }

    this.initialize();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.controller?.abort();
    this.fallback?.removeEventListener('click', this.handleRetry);
    this.previousFallback?.removeEventListener('click', this.handlePreviousRetry);
    window.removeEventListener('scroll', this.handleScroll);
    window.removeEventListener('pagehide', this.handlePageHide);
    window.removeEventListener('pageshow', this.handlePageShow);
    this.removeEventListener('click', this.handleItemClick, true);
    if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
  }

  async initialize() {
    if (this.historyEnabled && this.currentPage > 1) {
      await this.restorePreviousPages();
    }

    if (!this.isConnected) return;
    this.observeNextPage();
    this.queueVisiblePageUpdate();
  }

  get nextUrl() {
    return this.dataset.nextUrl || '';
  }

  set nextUrl(url) {
    if (url) {
      this.dataset.nextUrl = url;
      if (this.fallback) this.fallback.href = url;
    } else {
      delete this.dataset.nextUrl;
      this.fallback?.removeAttribute('href');
    }
  }

  get previousUrl() {
    return this.dataset.previousUrl || '';
  }

  set previousUrl(url) {
    if (url) {
      this.dataset.previousUrl = url;
      if (this.previousFallback) this.previousFallback.href = url;
    } else {
      delete this.dataset.previousUrl;
      this.previousFallback?.removeAttribute('href');
    }
  }

  get currentPage() {
    return Number(this.dataset.currentPage) || 1;
  }

  observeNextPage() {
    if (this.nextUrl) {
      this.observer?.observe(this.sentinel);
    } else {
      this.complete();
    }
  }

  async requestPage(url) {
    const requestUrl = new URL(url, location.origin);
    const key = this.dataset.infiniteScrollKey;
    if (key) requestUrl.searchParams.set('section_id', key);

    const controller = new AbortController();
    this.controller = controller;

    try {
      const response = await fetch(requestUrl.href, {
        headers: { 'X-Requested-With': 'XMLHttpRequest' },
        signal: controller.signal,
      });

      if (!response.ok) throw new Error(`Fetch failed with status ${response.status}`);

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const component = Array.from(doc.querySelectorAll('infinite-scroll')).find(
        (candidate) => candidate.dataset.infiniteScrollKey === key
      );
      const items = component?.querySelector('[data-infinite-scroll-items]');

      if (!component || !items) throw new Error('Infinite scroll response is missing its product grid');

      return { component, items: Array.from(items.children) };
    } finally {
      if (this.controller === controller) this.controller = null;
    }
  }

  async loadNextPage() {
    const url = this.nextUrl;
    if (!url || this.loading) return;

    this.loading = true;
    this.classList.add('state--loading');
    this.setAttribute('aria-busy', 'true');
    this.observer?.unobserve(this.sentinel);
    this.clearError();

    try {
      const { component, items } = await this.requestPage(url);
      const fragment = document.createDocumentFragment();
      items.forEach((item) => fragment.appendChild(item));
      this.items.appendChild(fragment);

      this.nextUrl = component.dataset.nextUrl || '';
      const count = this.getProductCount(items);
      this.updateStatus(count, 'more');
      this.afterItemsChanged(items, 'next');

      if (this.nextUrl) {
        this.observer?.observe(this.sentinel);
      } else {
        this.complete();
      }
    } catch (error) {
      if (error.name !== 'AbortError') this.showError();
    } finally {
      this.loading = false;
      this.classList.remove('state--loading');
      this.removeAttribute('aria-busy');
    }
  }

  async restorePreviousPages() {
    if (!this.historyEnabled || !this.previousUrl || this.restoring) return;

    this.restoring = true;
    this.classList.add('state--restoring');
    this.setAttribute('aria-busy', 'true');
    if (this.previousFallback) this.previousFallback.hidden = true;

    const savedState = this.readHistoryState();
    let anchor = this.getSavedAnchor(savedState)
      || this.items.querySelector('[data-infinite-scroll-product]')
      || this.items.firstElementChild;

    await this.nextLayout();
    const restoreState = savedState || {
      anchor: anchor?.dataset.infiniteScrollAnchor || '',
      anchorOffset: anchor?.getBoundingClientRect().top ?? null,
      scrollY: window.scrollY,
    };

    if (anchor && savedState?.anchor && Number.isFinite(savedState.anchorOffset)) {
      const delta = anchor.getBoundingClientRect().top - savedState.anchorOffset;
      window.scrollBy(0, delta);
    }

    const visited = new Set();
    let url = this.previousUrl;

    try {
      while (url && !visited.has(url) && this.isConnected) {
        visited.add(url);
        const before = anchor?.getBoundingClientRect().top;
        const { component, items } = await this.requestPage(url);
        const fragment = document.createDocumentFragment();
        items.forEach((item) => fragment.appendChild(item));
        this.items.prepend(fragment);
        this.previousUrl = component.dataset.previousUrl || '';
        this.refreshDynamicContent();
        await this.nextLayout();

        if (anchor && Number.isFinite(before)) {
          const after = anchor.getBoundingClientRect().top;
          window.scrollBy(0, after - before);
        }

        this.updateStatus(this.getProductCount(items), 'previous');
        this.afterItemsChanged(items, 'previous', false);
        url = this.previousUrl;
      }

      if (!anchor && Number.isFinite(savedState?.scrollY)) {
        window.scrollTo(0, savedState.scrollY);
      }

      this.previousUrl = '';
      if (this.previousFallback) this.previousFallback.hidden = true;
      await this.restoreSavedPosition(restoreState);
    } catch (error) {
      if (error.name !== 'AbortError') {
        if (this.previousFallback) this.previousFallback.hidden = false;
        if (this.status) this.status.textContent = 'Earlier products could not be restored automatically';
      }
    } finally {
      this.restoring = false;
      this.classList.remove('state--restoring');
      this.removeAttribute('aria-busy');
      this.queueVisiblePageUpdate();
    }
  }

  getProductCount(items) {
    const products = items.filter((item) => item.matches('[data-infinite-scroll-product]'));
    return products.length || items.length;
  }

  updateStatus(count, direction) {
    if (!this.status || !count) return;
    const label = count === 1 ? 'product' : 'products';
    this.status.textContent = direction === 'previous'
      ? `${count} previous ${label} restored`
      : `${count} more ${label} loaded`;
  }

  afterItemsChanged(items, direction, refresh = true) {
    if (refresh) this.refreshDynamicContent();
    const count = this.getProductCount(items);
    const page = Number(items.find((item) => item.dataset.infiniteScrollPage)?.dataset.infiniteScrollPage) || null;
    this.dispatchEvent(new CustomEvent('infinitescroll:loaded', {
      bubbles: true,
      detail: { count, direction, page, nextUrl: this.nextUrl },
    }));
    this.queueVisiblePageUpdate();
  }

  refreshDynamicContent() {
    if (typeof window.initializeBackgroundImages === 'function') {
      window.initializeBackgroundImages(this.items);
    }

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (typeof alignProductCardsHeight === 'function') alignProductCardsHeight(this.items);
      });
    });
  }

  clearError() {
    this.sentinel?.classList.remove('state--error');
    if (this.fallback) this.fallback.hidden = true;
  }

  showError() {
    this.sentinel?.classList.add('state--error');
    if (this.fallback) this.fallback.hidden = false;
    if (this.status) this.status.textContent = 'More products could not be loaded automatically';
  }

  complete() {
    this.observer?.disconnect();
    if (this.sentinel) this.sentinel.hidden = true;
  }

  queueVisiblePageUpdate() {
    if (!this.historyEnabled || this.restoring || this.scrollFrame) return;
    this.scrollFrame = requestAnimationFrame(() => {
      this.scrollFrame = null;
      const item = this.getVisiblePageItem();
      const page = Number(item?.dataset.infiniteScrollPage);
      if (!page || page === this.activePage) return;
      this.activePage = page;
      this.updateHistoryUrl(page);
      this.saveHistoryState();
    });
  }

  getVisiblePageItem() {
    const reference = window.innerHeight * 0.4;
    const coverage = new Map();

    for (const item of this.items.querySelectorAll(':scope > [data-infinite-scroll-page]')) {
      const rect = item.getBoundingClientRect();
      if (rect.top > reference || rect.bottom < reference) continue;
      const page = item.dataset.infiniteScrollPage;
      const current = coverage.get(page) || { width: 0, item };
      current.width += rect.width;
      coverage.set(page, current);
    }

    if (!coverage.size) return this.getVisibleItem();

    let visible = null;
    for (const [page, value] of coverage) {
      if (!visible || value.width > visible.width) visible = { ...value, page };
      if (value.width === visible.width && Number(page) === this.activePage) visible = { ...value, page };
    }

    return visible.item;
  }

  getVisibleItem(selector = '[data-infinite-scroll-page]') {
    const reference = window.innerHeight * 0.4;
    let candidate = null;

    for (const item of this.items.querySelectorAll(`:scope > ${selector}`)) {
      const rect = item.getBoundingClientRect();
      if (rect.bottom <= 0) continue;
      candidate = item;
      if (rect.top <= reference && rect.bottom >= reference) return item;
      if (rect.top > reference) return item;
    }

    return candidate;
  }

  updateHistoryUrl(page) {
    const url = new URL(location.href);
    if (page > 1) {
      url.searchParams.set('page', page);
    } else {
      url.searchParams.delete('page');
    }
    history.replaceState({ ...history.state, infiniteScrollPage: page }, '', url);
  }

  saveClickedProduct(event) {
    const link = event.target.closest('a[href]');
    const product = event.target.closest('[data-infinite-scroll-product]');
    if (!link || !product || !this.items.contains(product)) return;

    const page = Number(product.dataset.infiniteScrollPage) || this.activePage;
    this.activePage = page;
    this.updateHistoryUrl(page);
    this.saveHistoryState(product);
  }

  saveHistoryState(product = null) {
    if (!this.historyEnabled) return;
    const page = product
      ? Number(product.dataset.infiniteScrollPage) || this.activePage || this.currentPage
      : this.activePage || this.currentPage;
    const anchor = product
      || this.getVisibleItem(`[data-infinite-scroll-product][data-infinite-scroll-page="${page}"]`)
      || this.getVisibleItem('[data-infinite-scroll-product]');
    const state = {
      page,
      anchor: anchor?.dataset.infiniteScrollAnchor || '',
      anchorOffset: anchor?.getBoundingClientRect().top ?? null,
      scrollY: window.scrollY,
    };

    try {
      sessionStorage.setItem(this.historyStorageKey(), JSON.stringify(state));
    } catch (_) {}
  }

  get isReturnVisit() {
    const type = performance.getEntriesByType('navigation')[0]?.type;
    return type === 'back_forward' || type === 'reload';
  }

  readHistoryState() {
    if (!this.isReturnVisit) return null;

    return this.readSavedState();
  }

  readSavedState() {
    try {
      const value = sessionStorage.getItem(this.historyStorageKey());
      const state = value ? JSON.parse(value) : null;
      return state?.page === this.requestedPage ? state : null;
    } catch (_) {
      return null;
    }
  }

  get requestedPage() {
    return Number(new URL(location.href).searchParams.get('page')) || 1;
  }

  async restoreSavedPosition(savedState = this.readHistoryState()) {
    if (!savedState) return;

    if (document.fonts?.ready) await document.fonts.ready;
    await this.nextLayout();
    const anchor = this.getSavedAnchor(savedState);

    if (anchor && Number.isFinite(savedState.anchorOffset)) {
      window.scrollBy(0, anchor.getBoundingClientRect().top - savedState.anchorOffset);
    } else if (Number.isFinite(savedState.scrollY)) {
      window.scrollTo(0, savedState.scrollY);
    }
  }

  getSavedAnchor(state) {
    if (!state?.anchor) return null;
    return this.items.querySelector(`[data-infinite-scroll-anchor="${CSS.escape(state.anchor)}"]`);
  }

  historyStorageKey() {
    const url = new URL(location.href);
    url.searchParams.delete('page');
    url.searchParams.delete('section_id');
    url.searchParams.sort();
    return `infinite-scroll:${url.pathname}?${url.searchParams.toString()}`;
  }

  nextLayout() {
    return new Promise((resolve) => {
      requestAnimationFrame(() => requestAnimationFrame(resolve));
    });
  }
}

if (!customElements.get('infinite-scroll')) {
  customElements.define('infinite-scroll', InfiniteScroll);
}
